import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, AlertTriangle, Loader2, MessageCircle, Phone } from "lucide-react";
import { api } from "@/lib/api";

const waLink = (n) => { if (!n) return null; const d = String(n).replace(/\D/g, ""); return `https://wa.me/${d.length === 10 ? "91" + d : d}`; };
const telLink = (n) => (n ? `tel:${String(n).replace(/[^\d+]/g, "")}` : null);
const fmtDate = (d) => String(d || "").slice(0, 10);

export function FollowUpsPanel() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/admin/followups", { params: { scope: "overdue" } }).then((r) => r.data.map((f) => ({ ...f, overdue: true }))).catch(() => []),
      api.get("/admin/followups", { params: { scope: "today" } }).then((r) => r.data.map((f) => ({ ...f, overdue: false }))).catch(() => []),
    ]).then(([overdue, today]) => setItems([...overdue, ...today]));
  }, []);

  return (
    <div data-testid="followups-panel" className="mt-8">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold text-slate-900">Follow-ups Today & Overdue</h2>
        <Link to="/admin/crm-leads" className="text-sm font-semibold text-brand">View all leads →</Link>
      </div>

      {items === null ? <div className="mt-4 flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : items.length === 0 ? (
        <div className="mt-3 rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          <CalendarClock className="mx-auto mb-2 text-slate-300" size={28} />
          Nothing due today and no overdue follow-ups. You're all caught up.
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {items.map((f) => (
            <div key={f.id} data-testid={`followup-item-${f.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {f.overdue ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700"><AlertTriangle size={11} /> OVERDUE</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700"><CalendarClock size={11} /> TODAY</span>
                  )}
                  <span className="text-xs text-slate-400">Due {fmtDate(f.next_followup_date)}</span>
                </div>
                <p className="mt-1 truncate font-semibold text-slate-900">
                  {f.lead ? f.lead.lead_code : "Lead"} — {f.lead?.machine_name || ""}
                </p>
                <p className="text-sm text-slate-500">
                  {f.type} • Buyer: {f.lead?.buyer_name || "—"} • {f.lead?.buyer_mobile || ""}
                </p>
                {f.notes && <p className="text-xs text-slate-400">"{f.notes}"</p>}
              </div>
              <div className="flex shrink-0 gap-1.5">
                {f.lead && telLink(f.lead.buyer_mobile) && <a href={telLink(f.lead.buyer_mobile)} className="rounded-lg border border-slate-200 p-2 text-slate-600"><Phone size={14} /></a>}
                {f.lead && waLink(f.lead.buyer_whatsapp) && <a href={waLink(f.lead.buyer_whatsapp)} target="_blank" rel="noreferrer" className="rounded-lg bg-whatsapp p-2 text-white"><MessageCircle size={14} /></a>}
                <Link to="/admin/crm-leads" className="rounded-full border border-brand/30 bg-brand/5 px-3 py-1.5 text-xs font-bold text-brand">Open</Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
